import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import MapIcon from '@mui/icons-material/Map';
import SettingsIcon from '@mui/icons-material/Settings';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import WifiIcon from '@mui/icons-material/Wifi';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import LoggedUserInfo from './user-info';
import { useAuthContext } from '../../contexts/authContext';
import { useGetDeviceTypes } from '../../hooks/device-types/list-device-types';
import logoPoseidonFull from '../../images/logo-dark.png';
import logoPoseidonSmall from '../../images/logo_poseidon_city_small.svg';
import { AppContext } from '../../pages/layout';

type TMenuItem = {
  id: string;
  title: string;
  icon: JSX.Element;
  url: string;
  submenu?: TSubmenuItems;
  isVisible: boolean;
};

type TSubmenuItem = {
  id: string;
  title: string;
  url: string;
  color?: string;
};

type TMenuItems = TMenuItem[];
type TSubmenuItems = TSubmenuItem[];

const DRAWER_WIDTH: number = 230;

function Nav(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { isActionAllowed } = useAuthContext();

  const { pathname: activePage } = useLocation();

  const {
    isLoading: deviceTypesIsLoading,
    isError: deviceTypesIsError,
    data: deviceTypesData,
    refetch: refetchDeviceTypes,
  } = useGetDeviceTypes(99, 1);

  const appContext = useContext(AppContext);
  const open: boolean = appContext?.toggle ?? false;

  //Styles
  const dropFromTop = {
    left: open ? '219px' : '64px',
    top: '0',
  };

  const fixedNav = {
    top: 0,
    left: 0,
    bottom: 0,
    width: '65px',
    height: '100vh',
    background: '#fff',
    position: 'fixed',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1100,
    borderRight: '1px solid rgba(0, 0, 0, 0.12)',
    ...(open && {
      width: '220px',
    }),
  };

  //Submenu definition
  const [deviceSubmenu, setDeviceSubmenu] = useState<TSubmenuItems>([]);

  //MenuItems definition
  const MainMenu: TMenuItems = [
    {
      id: 'dashboard',
      title: t('navigation.dashboard'),
      icon: <DesktopWindowsIcon />,
      url: '/dashboard',
      isVisible: true,
    },
    { id: 'map', title: t('navigation.map'), icon: <MapIcon />, url: '/map', isVisible: true },
    {
      id: 'plans',
      title: t('navigation.plans'),
      icon: <CalendarTodayIcon />,
      url: '/daily-plans',
      isVisible: isActionAllowed('plan', 'read'),
    },
    {
      id: 'devices',
      title: t('navigation.devices'),
      icon: <LightbulbIcon />,
      url: '/devices',
      submenu: deviceSubmenu,
      isVisible: isActionAllowed('device', 'read'),
    },
    {
      id: 'groups',
      title: t('navigation.groups'),
      icon: <AccountTreeIcon />,
      url: '/groups',
      isVisible: isActionAllowed('group', 'read'),
    },
    {
      id: 'areas',
      title: t('navigation.areas'),
      icon: <ViewInArIcon />,
      url: '/areas',
      isVisible: isActionAllowed('area', 'read'),
    },
    {
      id: 'gateways',
      title: t('navigation.gateways'),
      icon: <WifiIcon />,
      url: '/gateways',
      isVisible: isActionAllowed('gateway', 'read'),
    },
    {
      id: 'reports',
      title: t('navigation.reports'),
      icon: <SummarizeIcon />,
      url: '/reports',
      isVisible: isActionAllowed('report', 'read'),
    },
    {
      id: 'settings',
      title: t('navigation.settings'),
      icon: <SettingsIcon />,
      url: '/settings',
      isVisible: true,
    },
  ];

  const sidebarRef = useRef<HTMLInputElement | null>(null);
  const contentRef = useRef<HTMLInputElement | null>(null);
  const navHeadRef = useRef<HTMLInputElement | null>(null);
  const navFootRef = useRef<HTMLInputElement | null>(null);
  const [scrollY, setScrollY] = useState(0);
  const [isMouseOver, setIsMouseOver] = useState(false);

  const updateSidebarScroll = (): void => {
    if (sidebarRef.current && isMouseOver) {
      const sidebar = sidebarRef.current;
      const header = navHeadRef.current;
      const footer = navFootRef.current;
      if (header instanceof HTMLElement && footer instanceof HTMLElement) {
        const sidebarHeight = sidebar.offsetHeight;
        const headerHeight = header.offsetHeight;
        const footerHeight = footer.offsetHeight;
        const availableHeight = sidebarHeight - headerHeight - footerHeight;

        if (contentRef.current) {
          const content = contentRef.current;
          const contentHeight = content.scrollHeight;
          if (contentHeight > availableHeight) {
            const maxScroll = contentHeight - availableHeight;
            content.style.transform = `translateY(-${scrollY}px)`;
            content.style.transition = 'transform 0.1s ease';

            if (scrollY > maxScroll) {
              setScrollY(maxScroll);
            }
          } else {
            content.style.transform = 'translateY(0)';
            content.style.transition = 'transform 0.1s ease';
          }
        }
      }
    }
  };

  const handleScroll = (): void => {
    if (isMouseOver) {
      setScrollY(window.scrollY);
    }
  };

  const handleMouseEnter = (): void => {
    setIsMouseOver(true);
  };

  const handleMouseLeave = (): void => {
    setIsMouseOver(false);
  };

  useEffect(() => {
    updateSidebarScroll();
    window.addEventListener('resize', updateSidebarScroll);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('resize', updateSidebarScroll);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollY, isMouseOver]);

  useEffect(() => {
    if (!deviceTypesIsLoading && !deviceTypesIsError && deviceTypesData) {
      const devices: TSubmenuItems = [];
      deviceTypesData.data.map((deviceType) => {
        devices.push({
          id: deviceType.device_type_id,
          title: deviceType.name,
          url: `/devices/${deviceType.device_type_id}`,
          color: deviceType.color,
        });
      });
      devices.sort((a, b) => a.title.localeCompare(b.title));
      setDeviceSubmenu(devices);
    }
  }, [deviceTypesData, deviceTypesIsLoading, deviceTypesIsError]);

  useEffect(() => {
    refetchDeviceTypes();
  }, [i18n.language]);

  return (
    <Box sx={fixedNav} ref={sidebarRef}>
      <Box
        sx={{
          minHeight: '64px',
          position: 'relative',
          background: '#fff',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1111,
        }}
        className="header-content"
        ref={navHeadRef}
      >
        <img src={open ? logoPoseidonFull : logoPoseidonSmall} width={open ? '200' : '40'} alt="Poseidon City logo" />
      </Box>
      <Box
        sx={{ maxHeight: 'calc(100vh - 177px)', position: 'relative' }}
        className="nav-content"
        ref={contentRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {MainMenu.filter((item) => item.isVisible).map((item) => (
          <Tooltip key={item.id} title={open || item.id === 'devices' ? '' : item.title} placement="right" arrow>
            <Box className="dropdown">
              <Box
                component={item.id === 'devices' ? 'div' : Link}
                to={item.id === 'devices' ? undefined : item.url}
                className="dropbtn"
                sx={{
                  width: '100%',
                  pt: '15px',
                  pb: '10px',
                  pl: '20px',
                  ...(activePage.includes(item.url) &&
                    !item.submenu && {
                      background: '#f6f6f6',
                      borderLeft: '2px solid #ff9800',
                      pl: '18px',
                    }),
                  ...(item.id === 'devices' &&
                    activePage.includes('devices') && {
                      background: '#f6f6f6',
                      borderLeft: '2px solid #ff9800',
                      pl: '18px',
                    }),
                  position: 'relative',
                  ...(item.id === 'devices' && {
                    cursor: 'default',
                  }),
                }}
              >
                <Box sx={{ display: 'flex', columnGap: '10px', color: '#0000009a' }}>
                  <Box>{item.icon}</Box>
                  {open && (
                    <Box sx={{ fontSize: '13px', fontWeight: '500', mt: '3px', color: '#212121' }}>{item.title}</Box>
                  )}
                </Box>
              </Box>
              {item.submenu && (
                <Box className="dropdown-content" sx={dropFromTop}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {item.id === 'devices' && (
                      <>
                        <Box
                          sx={{
                            borderLeft: '3px solid #e4e4e4',
                            ...(activePage === '/devices' && {
                              background: '#e4e4e420',
                            }),
                            '&:hover': {
                              background: '#e4e4e430',
                            },
                            '& a:hover': { background: 'none', textDecoration: 'none' },
                          }}
                        >
                          <Link
                            to="/devices"
                            style={{
                              fontWeight: 500,
                            }}
                          >
                            {t('pages.devices.all_devices')}
                          </Link>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                      </>
                    )}
                    {item.submenu.map((subItem) => (
                      <Box
                        key={subItem.id}
                        sx={{
                          borderLeft: `3px solid ${subItem.color}`,
                          ...(activePage.includes(subItem.id) && {
                            background: `${subItem.color}20`,
                          }),
                          '&:hover': {
                            background: `${subItem.color}30`,
                          },
                          '& a:hover': { background: 'none', textDecoration: 'none' },
                        }}
                      >
                        <Link to={subItem.url}>{subItem.title}</Link>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Tooltip>
        ))}
      </Box>
      <LoggedUserInfo />
    </Box>
  );
}

export default Nav;
export { DRAWER_WIDTH, TMenuItem };
